import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/router";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import dynamic from "next/dynamic";
import { isEdge } from "react-device-detect";
import { useTranslation } from "next-i18next";

import { COOKIE_KEY_READER_FONT_SIZE, ReaderFontSizes } from "@/shared/utils/theme/theme";
import { settingsActions, systemActions } from "@/store/actions";
import Panel from "./Panel";
import {
  CN_EL_ARROW_PLAYER,
  CN_EL_READER_MODE,
  CN_EL_SWIPE_ARROWS,
  layoutIds,
  ReaderQueryParam,
} from "./Panel.constants";
import useCookiesWithOptions from "@/shared/hooks/useCookiesWithOptions";
import { getBookIdNumber, isSameBook } from "@/shared/utils/content/content";
import { createPrintWindow, makeReaderPrintTitle } from "@/shared/utils/print";
import { getBookIdWithPrefix } from "@/shared/utils/content/content";
import {
  selectorSystemIsTopPanelVisible,
  selectorSystemOnLine,
} from "@/shared/store/system/selectors";
import { selectorSetting } from "@/shared/store/settings/selectors";
import { useAudioContext } from "@/shared/contexts/AudioContext";
import useReaderPlayer from "@/shared/hooks/reader/useReaderPlayer";
import {
  findTargetId,
  createReaderActiveLink,
  findContentLinkById,
  findFirstVisibleLinkInActiveParaOrJustFirstVisible,
} from "@/shared/utils/reader/reader";
import { useModalDownload } from "@/components/modal/ModalDownload";
import useMediaQuery, { MEDIA_QUERIES } from "@/shared/hooks/useMediaQuery";
import { useWhatInputIsLastPointerMouse } from "@/shared/what-input/useWhatInput";
import { getContentPreviewBible } from "@/shared/api/content/common";
import { DATA_ATTR_PANEL_INDEX, DATA_ATTR_PARA_ID } from "@/shared/utils/reader/constants";
import useRefData from "@/shared/hooks/useRefData";
import { CONTENT_CLASSES } from "@/shared/utils/content/content.constants";
import { getAudioChapterByChapterId } from "@/shared/utils/player";
import { CN_ICON_VIEW } from "@/shared/components/IconView/IconView.constants";
import { EL_ID_TOP_PANEL } from "@/components/TopPanel/TopPanel.constants";
import EgwWebFont from "../../../assets/EgwWebFont";
import {
  EL_ID_PORTAL_HEADER_ACTIONS,
  EL_ID_PORTAL_HEADER_ACTIONS_LEFT,
} from "@/components/Header/HeaderLanding.constants";
import { SETTING } from "@/shared/utils/settings/constants";
import { selectorHistoryAllHistory } from "@/shared/store/library/selectors";
import { getEventCoordinates, isFocusTextInput } from "@/shared/utils/dom";
import { makeOption } from "@/shared/utils/select";
import { usePagePropsContext } from "@/contexts/pageProps";
import { IconButtonSettingToggleInML } from "@/shared/components/reader/IconButtonsSettings";
import { clearSelection, getSelectionCrossBrowser } from "@/shared/utils/selection";
import useReaderLinksNavigation from "@/shared/hooks/reader/useReaderLinksNavigation";
import IconButton from "@/shared/components/IconButton/IconButton";
import useListenReaderCustomEventTypes from "@/shared/hooks/reader/useListenReaderCustomEventTypes";
import useReaderSaveHistory from "@/shared/hooks/reader/useReaderSaveHistory";
import { selectorMainTreeBibleVersions } from "@/shared/store/mainTree/selectors";
import { selectorDownloadedBooksIds } from "@/shared/store/sw/selectorDownloadedBooksIds";
import ReaderTopPanelLoader from "./ReaderTopPanel/ReaderTopPanelLoader";
import { NEXT_URLS } from "@/utils/routes/routes.constants";

import styles from "./reader.module.scss";

const ReaderTopPanel = dynamic(() => import("@/components/Reader/ReaderTopPanel/ReaderTopPanel"), {
  ssr: false,
  loading: ReaderTopPanelLoader,
});

const AvailableLangPanel = dynamic(
  () => import("@/components/Reader/AvailableLangPanel/AvailableLangPanel"),
  { ssr: false },
);

const SearchPanel = dynamic(() => import("@/components/Reader/SearchPanel/SearchPanel"), {
  ssr: false,
});

const ShareSelectionModal = dynamic(
  () => import("@/components/Reader/ShareSelectionModal/ShareSelectionModal"),
  { ssr: false },
);

const PanelBookContents = dynamic(
  () => import("@/components/Reader/PanelBookContents/PanelBookContents"),
  { ssr: false },
);

const Portal = dynamic(() => import("@/shared/components/Portal/Portal"), { ssr: false });

const ReaderContext = React.createContext({});

const jsClassNamePanel = "next-reader-panel";

const Reader = ({ isMLHistoryChecked, panelsIds, panels, indexPanelActive, fontSizeReaderSSR }) => {
  const { book, toc, paraList, prevChapter, nextChapter, isLoading } =
    panels[indexPanelActive] || {};

  const paraListParasIds = useMemo(() => paraList?.paras?.map((p) => p.para_id), [paraList]);

  const {
    isLanding,
    isSetCONFLICT,
    isSetTESTIMONIES,
    isSetRPRAC,
    isSetLanding,
    bookTitles,
    landingBookId,
  } = usePagePropsContext();

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const panelsRef = useRef();
  const refPromiseFnUpdatePanels = useRef();
  const router = useRouter();
  const isLastInputMouse = useWhatInputIsLastPointerMouse();
  const [cookie, setCookie] = useCookiesWithOptions([COOKIE_KEY_READER_FONT_SIZE]);
  const {
    chapterId,
    isPlaying,
    playChapterAndShowPlayer,
    getPlayerCurrentTime,
    paragraphsAudioPositions,
  } = useAudioContext();
  const openModalDownload = useModalDownload();

  const isMobile = useMediaQuery(MEDIA_QUERIES.untilTablet);
  const isMobileOrTablet = useMediaQuery(MEDIA_QUERIES.untilDesktopAndDesktop);
  const baseBible = useSelector(selectorSetting(SETTING.baseBible.id));
  const onLine = useSelector(selectorSystemOnLine);
  const layoutRedux = useSelector(selectorSetting(SETTING.layout.id));
  const isTopPanelVisible = useSelector(selectorSystemIsTopPanelVisible);
  const currentLayout =
    panelsIds.length > 1 ? (isMobile ? layoutIds.l2h : layoutRedux || layoutIds.l2h) : layoutIds.l1;
  const downloadedBooksIds = useSelector(selectorDownloadedBooksIds);
  const allHistory = useSelector(selectorHistoryAllHistory);
  const syncPlayerAndReader = useSelector(selectorSetting(SETTING.syncPlayerAndReader.id));
  const syncByTranslations = useSelector(selectorSetting(SETTING.showTranslations.id));
  const syncReader = useSelector(selectorSetting(SETTING.syncReader.id));
  const bibles = useSelector(selectorMainTreeBibleVersions);
  const biblesIds = useMemo(() => {
    return bibles.map((b) => getBookIdWithPrefix(b.id));
  }, [bibles]);
  const bookHistory = useMemo(() => {
    return book?.id && allHistory.find((bookDataItem) => bookDataItem.id === book.id);
  }, [book?.id, allHistory]);

  const idPanelMain = panelsIds[0];
  const idPanelSecond = panelsIds[1];
  const idPanelActive = panelsIds[indexPanelActive];

  const activeChapterId = useMemo(() => {
    if (!toc || !paraList) {
      return undefined;
    }

    const paraSuitable = paraList.paras?.find((p) => p.para_id === idPanelActive);
    return paraSuitable ? findTargetId(paraSuitable, toc) : undefined;
  }, [paraList, toc, idPanelActive]);

  const readerPlayerHookInstance = useReaderPlayer({
    bookHistory,
    book,
    toc,
    index: indexPanelActive,

    activeChapterId: activeChapterId,
    activeId: idPanelActive,

    fromML: true,
    isIgnoreSettings: true,

    updateActivePanel: async (idActiveNew) => {
      await refPromiseFnUpdatePanels.current;
      await updatePanel(indexPanelActive, idActiveNew);
    },
  });

  const { handlePlayClick, currentPlayingPara } = readerPlayerHookInstance;

  const handleSearchClick = () => {
    router.push({
      pathname: NEXT_URLS.searchLocal,
    });
  };

  const [shareSelection, setShareSelection] = useState("");
  const [linksActive, setLinksActive] = useState({});
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const setLinkActive = useCallback((index, link) => {
    setLinksActive((valueOld) => {
      return {
        ...valueOld,
        [index]: link,
      };
    });
  }, []);

  const cookieFontSize = parseInt(cookie[COOKIE_KEY_READER_FONT_SIZE]);
  let fontSizeFinal = ReaderFontSizes.default;

  if (cookieFontSize && !isNaN(cookieFontSize)) {
    fontSizeFinal = cookieFontSize;
  } else if (fontSizeReaderSSR && !isNaN(fontSizeReaderSSR)) {
    fontSizeFinal = fontSizeReaderSSR;
  }

  const inLibrary = bookHistory?.inLibrary || false;
  const isPlayingCurrentBook = isPlaying && getBookIdWithPrefix(chapterId) === book?.id;

  const isDownloaded = book && downloadedBooksIds.includes(book.book_id?.toString());

  const { saveRead, saveListen } = useReaderSaveHistory(
    idPanelActive,
    inLibrary,
    activeChapterId,
    readerPlayerHookInstance.isInPlayerAnotherBook,
    isMLHistoryChecked,
    isLanding,
  );

  const refData = useRefData({
    router,
    isLanding,
    panelsIds,
    indexPanelActive,
    book,
    toc,
    panels,
    paraList,
    activeChapterId,
    baseBible,
    downloadedBooksIds,
    onLine,
    fontSizeFinal,
    syncReader,
    isTopPanelVisible,
    currentLayout,
    syncPlayerAndReader,
    inLibrary,
    isPlaying,
    chapterId,
    isPlayingCurrentBook,
    paragraphsAudioPositions,
    currentPlayingPara,
    biblesIds,
  });

  const getElPanelByIndex = (indexPanel) => {
    return document.querySelector(`[${DATA_ATTR_PANEL_INDEX}="${indexPanel}"]`);
  };

  const scrollParaIntoView = (indexPanel, idPara) => {
    const elPanel = getElPanelByIndex(indexPanel);
    if (elPanel) {
      const elPara = elPanel.querySelector(`[${DATA_ATTR_PARA_ID}="${idPara}"]`);
      if (elPara) {
        elPara.scrollIntoView();
      }
    }
  };

  const getElementPanelActive = () => {
    return getElPanelByIndex(indexPanelActive);
  };

  const getReaderSelection = () => {
    const windowSelection = getSelectionCrossBrowser();

    if (windowSelection?.anchorNode?.parentNode?.closest(`.${CONTENT_CLASSES.PARAGRAPH}`)) {
      const selectionText = windowSelection.toString().trim();

      if (selectionText) {
        return selectionText;
      }
    }

    return null;
  };

  const getActivePanelElementToCopyOrPrint = () => {
    const elActivePanel = getElementPanelActive();

    if (elActivePanel) {
      const elPanelClone = elActivePanel.cloneNode(true);

      const elNoteReaderMode = elPanelClone.querySelector("." + CN_EL_READER_MODE);
      if (elNoteReaderMode) {
        elNoteReaderMode.remove();
      }

      const elArrowPlayer = elPanelClone.querySelector("." + CN_EL_ARROW_PLAYER);
      if (elArrowPlayer) {
        elArrowPlayer.remove();
      }

      const elSwipeArrows = elPanelClone.querySelector("." + CN_EL_SWIPE_ARROWS);
      if (elSwipeArrows) {
        elSwipeArrows.remove();
      }

      const elsIcons = elPanelClone.querySelectorAll("." + CN_ICON_VIEW);
      if (elsIcons) {
        elsIcons.forEach((i) => i.remove());
      }

      return elPanelClone;
    }

    return "";
  };

  const getPrintSelection = () => {
    const readerSelection = getReaderSelection();
    if (readerSelection) {
      return readerSelection;
    }

    const elActivePanel = getActivePanelElementToCopyOrPrint();
    if (elActivePanel) {
      return elActivePanel.innerHTML;
    }

    return "";
  };

  const getCopySelection = (useFallback) => {
    const readerSelection = getReaderSelection();
    if (readerSelection) {
      return readerSelection;
    }

    if (useFallback) {
      const elActivePanel = getActivePanelElementToCopyOrPrint();
      if (elActivePanel) {
        return elActivePanel.outerText;
      }
    }

    return "";
  };

  const copy = (useFallback = true, event) => {
    const copySelection = getCopySelection(useFallback);
    if (copySelection) {
      dispatch(
        systemActions.actionCopy(
          copySelection,
          event
            ? {
                copyEvent: event,
              }
            : undefined,
        ),
      );
    }
  };

  const print = () => {
    const paraFirst = paraList?.paras?.[0];
    createPrintWindow(
      getPrintSelection(),
      makeReaderPrintTitle({
        refcode_short: paraFirst.refcode_short,
        title: paraFirst.content,
      }),
    );
  };

  const updatePanels = useCallback(
    async (idsPanels, indexPanelActiveNew, queryParamsAdditional = undefined) => {
      const { indexPanelActive, router } = refData.current;
      clearSelection();

      const queryNew = {
        ...router.query,
        [ReaderQueryParam.panelFirst]: idsPanels[0],
        ...queryParamsAdditional,
      };

      const indexPanelActiveFinal = indexPanelActiveNew ?? indexPanelActive;

      if (indexPanelActiveFinal) {
        queryNew[ReaderQueryParam.indexPanelActive] = indexPanelActiveFinal;
      } else {
        delete queryNew[ReaderQueryParam.indexPanelActive];
      }

      const panelLinkFinal = idsPanels[1];

      if (panelLinkFinal) {
        queryNew[ReaderQueryParam.panelSecond] = panelLinkFinal;
      } else {
        delete queryNew[ReaderQueryParam.panelSecond];
      }

      if (
        !queryNew[ReaderQueryParam.indexPanelActive] ||
        queryNew[ReaderQueryParam.indexPanelActive] !== indexPanelActive ||
        !queryNew[ReaderQueryParam.panelFirst] ||
        queryNew[ReaderQueryParam.panelFirst] !== idPanelMain ||
        !queryNew[ReaderQueryParam.panelSecond] ||
        queryNew[ReaderQueryParam.panelSecond] !== idPanelSecond
      ) {
        refPromiseFnUpdatePanels.current = router.push(
          {
            query: queryNew,
          },
          undefined,
          {
            shallow: true,
          },
        );

        await refPromiseFnUpdatePanels.current;
      }
    },
    [],
  );

  const updateIndexPanelActive = useCallback(
    async (indexPanelActiveNew) => {
      await updatePanels(refData.current.panelsIds, indexPanelActiveNew);
    },
    [updatePanels],
  );

  const openSecondPanel = useCallback(
    async (id, indexPanelActiveNew) => {
      const { onLine, downloadedBooksIds, currentLayout } = refData.current;

      if (onLine || downloadedBooksIds.includes(getBookIdNumber(id).toString())) {
        if (currentLayout === layoutIds.l1) {
          dispatch(
            settingsActions.updateSettings({
              [SETTING.layout.id]: layoutIds.l2h,
            }),
          );
        }

        await refPromiseFnUpdatePanels.current;
        const { panelsIds } = refData.current;
        await updatePanels([panelsIds[0], id], indexPanelActiveNew);
      } else {
        dispatch(systemActions.toast("@sw:bookNotAvailableOffline"));
      }
    },
    [updatePanels],
  );

  const openLink = useCallback(
    async (
      id,
      idPara,
      isLinkTypeBible,
      // Don't change active split, if link of main split is already opened.
      skipChangeActive = refData.current.panelsIds.length > 1,
    ) => {
      const {
        baseBible,
        downloadedBooksIds,
        indexPanelActive,
        onLine,
        syncReader,
        currentLayout,
        panelsIds,
        biblesIds,
      } = refData.current;
      let idFinal = id;

      const id2ndSplit = getBookIdWithPrefix(panelsIds[1]);
      const isBibleIn2ndSplit = biblesIds.some((id) => isSameBook(id, id2ndSplit));
      const targetBible = isBibleIn2ndSplit ? id2ndSplit : baseBible;

      // Fetch bible link concordance, based on user's {baseBible}.
      if (onLine && isLinkTypeBible && !isSameBook(idFinal, targetBible)) {
        const [bookId, paraId] = idFinal.split(".");
        const bibleContent = await getContentPreviewBible(bookId, [targetBible], [paraId]);

        if (bibleContent?.[0]) {
          idFinal = bibleContent[0].para_id;
        }
      }
      // ============================================

      if (onLine || downloadedBooksIds.includes(getBookIdNumber(idFinal).toString())) {
        await openSecondPanel(idFinal, skipChangeActive ? indexPanelActive : 1);

        if (!syncReader) {
          dispatch(settingsActions.updateSettings(SETTING.syncReader.id, true));
        }

        const newLinkData = findContentLinkById(idPara, 0, id);

        let newActiveLink;
        if (newLinkData) {
          newActiveLink = createReaderActiveLink(
            newLinkData.isBible ? CONTENT_CLASSES.EGW_LINK_BIBLE : CONTENT_CLASSES.EGW_LINK_BOOK,
            newLinkData.id,
            newLinkData.index,
            newLinkData.paraId,
          );
        } else {
          newActiveLink = null;
        }
        setLinkActive(0, newActiveLink);

        if (currentLayout === layoutIds.l1) {
          dispatch(
            settingsActions.updateSettings({
              [SETTING.layout.id]: layoutIds.l2h,
            }),
          );
        }
      } else {
        dispatch(systemActions.toast("@sw:bookNotAvailableOffline"));
      }
    },
    [setLinkActive, openSecondPanel],
  );

  const updatePanel = useCallback(
    async (indexPanel, id, options) => {
      let updatePlayerChapterToo, skipChangeIndexPanelActive, queryParamsAdditional;

      if (options) {
        updatePlayerChapterToo = options.updatePlayerChapterToo;
        skipChangeIndexPanelActive = options.skipChangeIndexPanelActive;
        queryParamsAdditional = options.queryParamsAdditional;
      }

      const { panelsIds, toc, paragraphsAudioPositions, indexPanelActive } = refData.current;

      if (updatePlayerChapterToo) {
        if (
          refData.current.isPlaying &&
          refData.current.syncPlayerAndReader &&
          getBookIdWithPrefix(id) === getBookIdWithPrefix(refData.current.chapterId) &&
          getAudioChapterByChapterId(toc, id)
        ) {
          const positionsChapterNew = paragraphsAudioPositions?.find((p) => p.id === id);
          playChapterAndShowPlayer(id, positionsChapterNew ? positionsChapterNew.start : 0);
        }
      }

      const idsPanelsNew = [...panelsIds];
      idsPanelsNew[indexPanel] = id;
      await updatePanels(
        idsPanelsNew,
        skipChangeIndexPanelActive ? indexPanelActive : indexPanel,
        queryParamsAdditional,
      );
    },
    [updatePanels],
  );

  const toggleLayout = () => {
    const newLayout = currentLayout === layoutIds.l2h ? layoutIds.l2v : layoutIds.l2h;
    dispatch(
      settingsActions.updateSettings({
        [SETTING.layout.id]: newLayout,
      }),
    );
  };

  const closePanel = useCallback(
    (indexPanel) => {
      if (indexPanel === undefined) {
        const { router, indexPanelActive, isLanding } = refData.current;
        const newQuery = { ...router.query };

        if (!isLanding) {
          if (indexPanelActive === 0) {
            newQuery[ReaderQueryParam.panelFirst] = newQuery[ReaderQueryParam.panelSecond];
          }
        }

        delete newQuery[ReaderQueryParam.panelSecond];
        delete newQuery[ReaderQueryParam.indexPanelActive];

        router.push(
          {
            pathname: router.pathname,
            query: newQuery,
          },
          undefined,
          { shallow: true },
        );
      } else {
        const { router, panelsIds } = refData.current;

        const routerQueryNew = {
          ...router.query,
          [ReaderQueryParam.panelFirst]: indexPanel === 0 ? panelsIds[1] : panelsIds[0],
        };

        delete routerQueryNew[ReaderQueryParam.panelSecond];
        delete routerQueryNew[ReaderQueryParam.indexPanelActive];

        router.push(
          {
            query: routerQueryNew,
          },
          undefined,
          { shallow: true },
        );
      }

      setLinkActive(0, null);
    },
    [setLinkActive],
  );

  const openModalDownloadActiveBook = () => {
    openModalDownload(book.book_id);
  };

  const openPanelTranslationsActiveBook = () => {
    dispatch(systemActions.setShowPanelBookTranslations(true));
  };

  const handleToggleBookInMLSuccess = (isInLibraryNew) => {
    if (isInLibraryNew) {
      saveRead(activeChapterId, idPanelActive, true);

      if (isPlayingCurrentBook) {
        saveListen(chapterId, getPlayerCurrentTime(), true);
      }
    }
  };

  const handleClickBtnContent = () => {
    dispatch(systemActions.setShowPanelBookContents(true));
  };

  // Updates share selection in the state.
  // Replaces browsers copying with Readers copying.
  useEffect(() => {
    let touchStartCoords = null;
    const touchStartListener = (e) => {
      touchStartCoords = getEventCoordinates(e);
    };
    const touchEndListener = (e) => {
      if (isEdge) {
        e.preventDefault(); // Prevent showing `edge mini menus`
      }

      const isDoubleClick = e.detail === 2;
      if (e.target?.closest("." + jsClassNamePanel) && !isDoubleClick) {
        const coords = getEventCoordinates(e);
        const isRegularClick =
          touchStartCoords && coords.x === touchStartCoords.x && coords.y === touchStartCoords.y;
        if (!isRegularClick) {
          setShareSelection(getCopySelection(true));
          
          // add share selected text
          if (document.body.classList.contains("selection-mode")) {
            const selectedText = getReaderSelection();
            if (selectedText) {
              // create link
              const currentUrl = new URL(window.location.href);
              currentUrl.searchParams.set("text", encodeURIComponent(selectedText));
              setShareUrl(currentUrl.toString());
              
              // Clear selection before showing modal to prevent jumping on mobile
              clearSelection();
              
              setShowShareModal(true);
              
              document.body.style.cursor = "";
              document.body.classList.remove("selection-mode");
            } else {
              // If no text selected, show toast and keep selection mode
              document.body.style.cursor = "text";
            }
          } else {
            copy(false);
          }
        }
      }
    };

    const copyListener = (e) => {
      // Prevent copy if the cursor is in a text field.
      if (isFocusTextInput()) {
        return;
      }

      copy(true, e);
    };

    // Add touch listeners always for mobile support
    document.addEventListener("touchstart", touchStartListener);
    document.addEventListener("touchend", touchEndListener);

    // Add mouse listeners only when mouse is being used
    if (isLastInputMouse) {
      document.addEventListener("mousedown", touchStartListener);
      document.addEventListener("mouseup", touchEndListener);
    }

    document.addEventListener("copy", copyListener);

    setShareSelection(getCopySelection(true));

    return () => {
      document.removeEventListener("touchstart", touchStartListener);
      document.removeEventListener("touchend", touchEndListener);

      if (isLastInputMouse) {
        document.removeEventListener("mousedown", touchStartListener);
        document.removeEventListener("mouseup", touchEndListener);
      }

      document.removeEventListener("copy", copyListener);
    };
  }, [isLastInputMouse, getCopySelection, copy]);

  // Resets active link if there is no link panel.
  const panelsCount = panelsIds.length;
  useEffect(() => {
    if (panelsCount < 2) {
      setLinkActive(0, null);
    }
  }, [panelsCount, setLinkActive]);

  useEffect(() => {
    if (refData.current.router.query.playOnLoad) {
      playChapterAndShowPlayer(activeChapterId, refData.current.router.query.pos || 0);
    }
  }, []);

  // Open link with selected text
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const textParam = urlParams.get("text");
    
    if (textParam) {
      const decodedText = decodeURIComponent(textParam);
      
      setTimeout(() => {
        const elPanel = getElementPanelActive();
        if (elPanel) {
          const walker = document.createTreeWalker(
            elPanel,
            NodeFilter.SHOW_TEXT,
            null,
            false
          );
          
          let node;
          while ((node = walker.nextNode())) {
            const text = node.textContent;
            const index = text.indexOf(decodedText);
            
            if (index !== -1) {
              const range = document.createRange();
              range.setStart(node, index);
              range.setEnd(node, index + decodedText.length);
              
              const selection = window.getSelection();
              selection.removeAllRanges();
              selection.addRange(range);
              
              const rect = range.getBoundingClientRect();
              window.scrollTo({
                top: window.scrollY + rect.top - window.innerHeight / 2,
                behavior: "smooth"
              });
              
              break;
            }
          }
        }
      }, 500);
    }
  }, [getElementPanelActive, router.query]);

  const syncLinkPanelToMainPanel = async (idToSearchLinksIn) => {
    if (syncReader && panels.length > 1 && indexPanelActive === 0) {
      const newLinkData = findFirstVisibleLinkInActiveParaOrJustFirstVisible(idToSearchLinksIn, 0);

      if (newLinkData) {
        await openLink(newLinkData.id, newLinkData.paraId, newLinkData.isBible, true);
      }
    }
  };

  const syncPanelsByTranslations = async () => {
    if ((syncByTranslations || isLanding) && panels.length > 1) {
      const indexPanelToUpdate = indexPanelActive === 0 ? 1 : 0;

      let idNew;
      if (paraList?.paras?.length) {
        let paraActiveIndex = paraList.paras.findIndex((p) => p.para_id === idPanelActive);

        if (paraActiveIndex === -1) {
          paraActiveIndex = 0;
        }

        let paraToSearchTranslationIn = paraList?.paras[paraActiveIndex];

        if (!paraToSearchTranslationIn.translations.length) {
          paraToSearchTranslationIn = paraList.paras.find((p) => p.translations.length);
        }

        if (paraToSearchTranslationIn) {
          const bookIdPanelToUpdate = getBookIdNumber(panelsIds[indexPanelToUpdate]);
          const translateSuitable = paraToSearchTranslationIn.translations.find((p) => {
            return isSameBook(p.para_id, bookIdPanelToUpdate);
          });

          if (translateSuitable) {
            idNew = translateSuitable.para_id;
          }
        }
      }

      if (idNew) {
        await refPromiseFnUpdatePanels.current;
        updatePanel(indexPanelToUpdate, idNew, {
          skipChangeIndexPanelActive: true,
        });
        return true;
      }
    }

    return false;
  };

  const syncPanels = async (firstVisiblePara) => {
    const success = await syncPanelsByTranslations();
    
    if (!success) {
      await syncLinkPanelToMainPanel(firstVisiblePara);
    }
  };

  const handleNewLink = (newLink) => {
    openLink(newLink.id, newLink.paraId, newLink.isBible, true);
  };

  useReaderLinksNavigation({
    idPara: idPanelMain,
    indexPanel: indexPanelActive,

    isOpenFirstIfNoLinkActive: panelsIds.length > 1,
    linkActive: linksActive[indexPanelActive],
    onNewLink: handleNewLink,
  });

  const readerBooks = panels.map((p) => p.book).filter((b) => !!b);

  const nBookIdFirst = getBookIdNumber(panelsIds[0]);

  const optionsFilterBooks = useMemo(() => {
    const booksIdsOpened = [];
    const booksLangsOpened = [];
    const options = [];

    // Include each opened book.
    for (const b of readerBooks) {
      if (!booksIdsOpened.includes(b.book_id)) {
        booksIdsOpened.push(b.book_id);
        booksLangsOpened.push(b.lang);
        options.push(makeOption(b.book_id, b.title));
      }
    }
    // ========================================

    // Include rest books from the related set of each opened book.
    if (isSetLanding && bookTitles) {
      for (const book of bookTitles) {
        if (booksLangsOpened.includes(book.lang) && !booksIdsOpened.includes(book.book_id)) {
          options.push(makeOption(book.book_id, book.title));
        }
      }
    }
    // ========================================

    return options;
  }, [isSetLanding, bookTitles, readerBooks]);

  useListenReaderCustomEventTypes({
    playEventHandler: handlePlayClick,
  });

  const jsxTopPanel = (
    <ReaderTopPanel
      isPanelLoading={isLoading}
      className={styles["reader-top-panel"]}
      isDownloaded={isDownloaded}
      isPlaying={isPlaying}
      inLibrary={inLibrary}
      isLanding={isLanding}
      landingBookId={landingBookId}
      currentLayout={currentLayout}
      shareSelection={shareSelection}
      paraList={paraList}
      book={book}
      toc={toc}
      prevChapter={prevChapter}
      nextChapter={nextChapter}
      indexPanelActive={indexPanelActive}
      bookTitles={bookTitles}
    />
  );

  return (
    <ReaderContext.Provider
      value={{
        indexPanelActive,
        idPanelMain: panelsIds[0],
        idPanelLink: panelsIds[1],
        idPanelActive,
        panelsIds,

        fontSizeFinal,

        book,
        paraListParasIds,

        copy: copy,
        print,

        openSecondPanel,
        openLink,
        updatePanel,
        updateIndexPanelActive,
        scrollParaIntoView,

        initOrPlayOrPauseAudio: handlePlayClick,
        toggleLayout,
        closePanel,
        openModalDownloadActiveBook,
        openPanelTranslationsActiveBook,
        handleToggleBookInMLSuccess,

        linksActive,

        syncPanels,

        readerPlayerHookInstance,
      }}>
      <div className={classNames(styles["reader"], "reader", book?.realType)}>
        {isMobileOrTablet ? (
          <Portal id={EL_ID_TOP_PANEL}>
            {isMobile && (
              <IconButton
                className={styles["btn-content"]}
                title={t("content")}
                icon={EgwWebFont.content}
                onClick={handleClickBtnContent}
              />
            )}
            {jsxTopPanel}
          </Portal>
        ) : (
          jsxTopPanel
        )}

        <div
          id="panelSplit"
          className={classNames(
            styles["panel-split"],
            styles[`layout${panelsIds.length}`],
            styles[currentLayout],
          )}
          ref={panelsRef}>
          {panels.map((panelData, index) => {
            if (!panelData) {
              return <div key={index} />;
            }

            return (
              <Panel
                key={index}
                currentLayout={currentLayout}
                className={classNames(jsClassNamePanel, styles["wrap-scroller"])}
                index={index}
                data={panelData}
                linkActive={linksActive[index]}
                active={indexPanelActive === index}
                fontSize={fontSizeFinal}
                prevChapter={prevChapter}
                nextChapter={nextChapter}
              />
            );
          })}
        </div>
      </div>

      <SearchPanel
        nBookIdFirst={isLanding ? nBookIdFirst : undefined}
        optionsFilterBooks={isLanding ? optionsFilterBooks : undefined}
      />
      <AvailableLangPanel />
      <PanelBookContents />

      {showShareModal && (
        <ShareSelectionModal
          url={shareUrl}
          onClose={() => {
            setShowShareModal(false);
            setShareUrl("");
          }}
        />
      )}

      <Portal id={EL_ID_PORTAL_HEADER_ACTIONS}>
        <div className={styles["header-actions"]}>
          {isMobile && (
            <>
              {!isSetCONFLICT && !isSetRPRAC && !isSetTESTIMONIES && (
                <IconButtonSettingToggleInML
                  isVariantBookmark
                  color={IconButton.color.headerItem}
                  book={book}
                  onToggleInMLSuccess={handleToggleBookInMLSuccess}
                />
              )}
              <IconButton
                className={styles["btn-play"]}
                title={t("player")}
                icon={isPlaying ? EgwWebFont["pause-circle"] : EgwWebFont["play-header"]}
                color={IconButton.color.headerItem}
                onClick={handlePlayClick}
              />

              {(isSetCONFLICT || isSetRPRAC || isSetTESTIMONIES) && (
                <IconButton
                  className={classNames(styles["btn-search"], { invisible: isSetTESTIMONIES })}
                  title={t("search")}
                  icon={EgwWebFont["search"]}
                  color={IconButton.color.headerItem}
                  onClick={handleSearchClick}
                />
              )}
            </>
          )}
        </div>
      </Portal>

      <Portal id={EL_ID_PORTAL_HEADER_ACTIONS_LEFT}>
        {isMobile && (isSetCONFLICT || isSetRPRAC || isSetTESTIMONIES) && (
          <IconButtonSettingToggleInML
            isVariantBookmark
            color={IconButton.color.headerItem}
            book={book}
            onToggleInMLSuccess={handleToggleBookInMLSuccess}
          />
        )}
      </Portal>
    </ReaderContext.Provider>
  );
};

Reader.propTypes = {
  panels: PropTypes.array,
  panelsIds: PropTypes.array,
  indexPanelActive: PropTypes.number,
  urlOrigin: PropTypes.string,
  landingBookId: PropTypes.string,
  fontSizeReaderSSR: PropTypes.number,
  isLanding: PropTypes.bool,
  isMLHistoryChecked: PropTypes.bool,
  bookTitles: PropTypes.array,
};

/**
 *
 * @returns {{
 *       indexPanelActive: number,
 *       idActive: string,
 *       idPanelMain: string,
 *       idPanelLink: string,
 *       idPanelActive: string,
 *       panelsIds: string[],

 *       fontSizeFinal: number,

 *       book: {}|null,
 *       paraListParasIds: number[]|undefined|null,

 *       copy: function,
 *       print: function,

 *       openSecondPanel: function,
 *       openLink: function,
 *       updatePanel: function,
 *       updateIndexPanelActive: function,
 *       scrollParaIntoView: function,

 *       initOrPlayOrPauseAudio: handlePlayClick: function,
 *       toggleLayout: function,
 *       closePanel: function,
 *       openModalDownloadActiveBook: function,
 *       handleToggleBookInMLSuccess: function,

 *       linksActive: {},

 *       syncPanels: function,

 *       readerPlayerHookInstance: ReaderPlayerHook,
 * }}
 */
export const useReaderContext = () => useContext(ReaderContext);

export default Reader;
